/**
 * Raise a ticket, or correct one.
 *
 * A sheet over the list, as legacy had it, and short: a title, what happened,
 * how urgent, whose work it is, when it is wanted by, and the files. What
 * legacy's form also carried and this does not: a Status field disabled for
 * everyone but one hard-coded email address, a System picker that only existed
 * because tickets belonged to tenants, and a duplicate of the assignment
 * controls the ticket page already owns.
 *
 * A save is a PATCH carrying the ticket as it was last read, so two people
 * editing one ticket cannot silently overwrite each other — which legacy, whose
 * form posted the whole record back with no precondition, had no answer for.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";

import { AttachmentPicker } from "@/components/helpdesk/attachments";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Modal } from "@/components/ui/dialog";
import { Field, Form, Input, Textarea } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { api, errorMessage, ifMatch } from "@/services/api";
import {
  type AttachmentDraft,
  DEPARTMENT_LABEL,
  DEPARTMENTS,
  HELPDESK_KEY,
  TICKETS_ENDPOINT,
  PRIORITIES,
  PRIORITY_LABEL,
  type Ticket,
  type TicketDepartment,
  type TicketPriority,
  type TicketType,
  TYPE_LABEL,
  TYPES,
  toAttachmentInput,
  uploadDrafts,
} from "@/services/helpdesk";
import { useTranslation } from "@/store/locale";

/** The footer's Save button reaches the form across the modal by this name. */
const FORM_ID = "helpdesk-ticket-form";

export function TicketForm({
  ticket,
  parentId,
  onClose,
  onSaved,
}: {
  /** Null to raise a new one. */
  ticket: Ticket | null;
  /** Set when the sheet is adding a task to this ticket. */
  parentId?: number | undefined;
  onClose: () => void;
  onSaved?: ((ticket: Ticket) => void) | undefined;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const creating = ticket === null;

  const [title, setTitle] = useState(ticket?.title ?? "");
  const [description, setDescription] = useState(ticket?.description ?? "");
  const [priority, setPriority] = useState<TicketPriority>(ticket?.priority ?? "medium");
  const [ticketType, setTicketType] = useState<string>(ticket?.ticket_type ?? "");
  const [department, setDepartment] = useState<string>(ticket?.department ?? "");
  const [dueDate, setDueDate] = useState(ticket?.due_date ?? "");
  const [files, setFiles] = useState<AttachmentDraft[]>(ticket?.attachments ?? []);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const missingTitle = title.trim().length === 0;
  const missingDescription = description.trim().length === 0;

  const save = useMutation({
    mutationFn: async () => {
      // The files go up first, on their own, as files. Only now — a file is
      // stored when somebody commits to the record, not when they pick it.
      const attachments = toAttachmentInput(await uploadDrafts(files));
      const body = {
        title: title.trim(),
        description: description.trim(),
        priority,
        ticket_type: (ticketType || null) as TicketType | null,
        department: (department || null) as TicketDepartment | null,
        due_date: dueDate || null,
        attachments,
      };
      if (creating) {
        return (
          await api.post<Ticket>("/helpdesk/tickets", {
            ...body,
            ...(parentId === undefined ? {} : { parent_id: parentId }),
          })
        ).data;
      }
      // `ifMatch` carries the ticket as it was last read, so a save cannot land
      // on top of somebody else's without being seen.
      return (await api.patch<Ticket>(`/helpdesk/tickets/${ticket.id}`, body, ifMatch(ticket)))
        .data;
    },
    onSuccess: async (saved) => {
      toast.success(creating ? t("hd.raised") : t("hd.saved"));
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      // The list is keyed by its endpoint rather than by `HELPDESK_KEY`, and
      // react-query matches a prefix element by element — `["/helpdesk"]` is
      // not a prefix of `["/helpdesk/tickets", …]`. Without this a ticket
      // raised here was not on the list behind it until a reload.
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
      onSaved?.(saved);
      onClose();
    },
    onError: (failure) => setError(errorMessage(failure, t("hd.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (missingTitle || missingDescription) return;
    setError(null);
    save.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        creating
          ? parentId === undefined
            ? t("hd.newTicket")
            : t("hd.newTask")
          : t("hd.editTicket")
      }
      description={creating ? t("hd.newTicketHint") : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={save.isPending}
            loadingLabel={t("new.saving")}
          >
            {creating ? t("hd.raise") : t("common.submit")}
          </Button>
        </>
      }
    >
      {error ? (
        <p
          role="alert"
          className="bg-danger-soft text-danger text-callout mb-4 flex items-start gap-2 rounded-lg px-3.5 py-2.5"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : null}

      <Form
        id={FORM_ID}
        noValidate
        onSubmit={submit}
        className="grid gap-4 pb-2 sm:grid-cols-2"
      >
        <Field
          label={t("hd.title")}
          required
          htmlFor="ticket-title"
          className="sm:col-span-2"
          {...(touched && missingTitle ? { error: t("hd.titleRequired") } : {})}
        >
          <Input
            id="ticket-title"
            value={title}
            maxLength={255}
            aria-invalid={touched && missingTitle ? true : undefined}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("hd.titlePlaceholder")}
          />
        </Field>

        <Field
          label={t("hd.description")}
          required
          htmlFor="ticket-description"
          className="sm:col-span-2"
          {...(touched && missingDescription ? { error: t("hd.descriptionRequired") } : {})}
        >
          <Textarea
            id="ticket-description"
            rows={4}
            value={description}
            aria-invalid={touched && missingDescription ? true : undefined}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("hd.descriptionPlaceholder")}
          />
        </Field>

        <Field label={t("hd.priority")} required htmlFor="ticket-priority">
          <OptionSelect
            id="ticket-priority"
            value={priority}
            onChange={(next) => setPriority(next as TicketPriority)}
            choices={PRIORITIES.map((value) => ({ value, label: t(PRIORITY_LABEL[value]) }))}
          />
        </Field>

        <Field label={t("hd.type")} htmlFor="ticket-type">
          <OptionSelect
            id="ticket-type"
            value={ticketType}
            onChange={setTicketType}
            clearable
            placeholder={t("hd.selectType")}
            choices={TYPES.map((value) => ({ value, label: t(TYPE_LABEL[value]) }))}
          />
        </Field>

        <Field label={t("hd.department")} htmlFor="ticket-department">
          <OptionSelect
            id="ticket-department"
            value={department}
            onChange={setDepartment}
            clearable
            placeholder={t("hd.selectDepartment")}
            choices={DEPARTMENTS.map((value) => ({
              value,
              label: t(DEPARTMENT_LABEL[value]),
            }))}
          />
        </Field>

        <Field label={t("hd.dueDate")} htmlFor="ticket-due-date">
          <DatePicker id="ticket-due-date" value={dueDate} onChange={setDueDate} />
        </Field>

        <Field
          label={t("hd.attachments")}
          htmlFor="ticket-attachments"
          className="sm:col-span-2"
        >
          <AttachmentPicker id="ticket-attachments" value={files} onChange={setFiles} />
        </Field>
      </Form>
    </Modal>
  );
}
